import Constants from 'expo-constants';
import { Platform } from 'react-native';
import {
  type configLoggerType,
  type defLvlType,
  logger as rnLogger,
  type transportFunctionType,
} from 'react-native-logs';

import { datadog } from '@sg/expo-datadog-client';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * "What's the deal with the transports + conditioning??!?"
 * ───────────────────────────────────────────────────────────────────────────
 *
 * The main issue is that, for NATIVE, we DON'T want `console.*`, because it has
 * perf issues.
 *
 * And so for NATIVE we have this custom `datadogTransport` that sends logs to
 * Datadog over the network.
 *
 * But we DON'T want that for WEB, because Datadog is ALREADY collecting
 * `console.*` AUTOMATICALLY.
 *
 * In other worse, for WEB, the `mapConsoleTransport` is sufficient and
 * `datadogTransport` only generates DUPLICATE logs.
 *
 * When we're doing NATIVE development locally, we might want to override this
 * behavior, so we can see logs in the console, so there's an env var for that.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * NOTE: There's an override var to enable console logging (local dev only)
 * ───────────────────────────────────────────────────────────────────────────
 * NOTE: Relevant web + logging options: `forwardErrorsToLogs`,
 * `forwardConsoleLogs`, `forwardReports`
 * https://docs.datadoghq.com/logs/log_collection/javascript/#initialization-parameters
 */

// ─────────────────────────────────────────────────────────────────────────────
const shouldIncludeDatadogTransport = Platform.OS !== 'web' || !Constants.expoConfig?.extra?.['GARNISH_LOGGER_ENABLE_NATIVE_CONSOLE_LOGGING_OVERRIDE']; // prettier-ignore
const shouldIncludeConsoleTransport = Platform.OS === 'web' || Boolean(Constants.expoConfig?.extra?.['GARNISH_LOGGER_ENABLE_NATIVE_CONSOLE_LOGGING_OVERRIDE']); // prettier-ignore
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// ── CONFIG ───────────────────────────────────────────────────────────────────

/**
 * The app-configurable subset of the underlying react-native-logs
 * `configLoggerType`. The remaining options (transports, formatting, levels)
 * are platform decisions owned by garnish-logger.
 *
 * Only options that react-native-logs consults live (or exposes setters for)
 * are included, because the logger singleton must exist at module load —
 * feature modules `extend()` it at import time, before any app code runs.
 * `enabledExtensions` is narrowed to `string[]`: the underlying `null` variant
 * means "every extension logs", which is how the first v5 rollout flooded
 * Datadog with dormant extension logs.
 */
export type GarnishLoggerConfig = Partial<
  Pick<UnderlyingLoggerConfig, 'severity' | 'enabled'> & { enabledExtensions: string[] }
>;

type UnderlyingLoggerConfig = configLoggerType<transportFunctionType<object>, defLvlType>;

export const defaultLoggerConfig: Required<GarnishLoggerConfig> = {
  severity: 'debug',
  enabled: true,
  enabledExtensions: [],
};

/**
 * Applies app-level logger config over `defaultLoggerConfig`. Call once from
 * the app's entry point; call again to replace the previous overrides.
 *
 * Extensions not listed in `enabledExtensions` stay silent. The whitelist is
 * consulted by name on every log call, so `extend()` and `initLogger()` may
 * run in any order (Metro inline requires can defer a feature module past app
 * init). The contract is pinned by logger.spec.ts.
 *
 * ```ts
 * initLogger({ enabledExtensions: ['CHECKOUT_MACHINE', 'ORDER_RECEIPT'] });
 *
 * // or extend the defaults / tune other underlying options:
 * initLogger({
 *   enabledExtensions: [...defaultLoggerConfig.enabledExtensions, 'CHECKOUT_MACHINE'],
 *   severity: 'info',
 * });
 * ```
 */
export function initLogger(config: GarnishLoggerConfig = {}) {
  const merged = { ...defaultLoggerConfig, ...config };

  enabledExtensions.splice(0, enabledExtensions.length, ...merged.enabledExtensions);
  logger.setSeverity(merged.severity);

  if (merged.enabled) {
    logger.enable();
  } else {
    logger.disable();
  }
}

/**
 * The live extension whitelist. react-native-logs v5 keeps a reference to the
 * array passed as `enabledExtensions` and consults it by name on every log
 * call (no registration requirement), which is what lets `initLogger()`
 * reconfigure the already-created singleton.
 */
const enabledExtensions: string[] = [...defaultLoggerConfig.enabledExtensions];

// ─────────────────────────────────────────────────────────────────────────────

// NOTE: There's this annoying typescript error from the react-native-logs
// library, that isn't disabled with `@ts-ignore` or `@ts-expect-error`.
// https://github.com/mowispace/react-native-logs/issues/102
// Using type assertion to avoid TypeScript error about private properties
export const logger = rnLogger.createLogger({
  printLevel: false,
  printDate: false,
  severity: defaultLoggerConfig.severity,
  enabled: defaultLoggerConfig.enabled,
  enabledExtensions,
  transport(props) {
    shouldIncludeConsoleTransport && mapConsoleTransport(props);
    shouldIncludeDatadogTransport && datadogTransport(props);
  },
}) as any;

// ─────────────────────────────────────────────────────────────────────────────
// ── TRANSPORTS ───────────────────────────────────────────────────────────────

const mapConsoleTransport: transportFunctionType<object> = (props) => {
  const consoleMethod = (console as any)[props.level.text] || console.log;

  consoleMethod(props.msg);

  return true;
};

const datadogTransport: transportFunctionType<object> = (props) => {
  const logLevel = props.level.text;

  // TODO: add support for different types
  const message = `${props.msg}`;

  switch (logLevel) {
    case 'info': {
      datadog.logger.info(message);
      break;
    }

    case 'debug': {
      datadog.logger.debug(message);
      break;
    }

    case 'warn': {
      datadog.logger.warn(message);
      break;
    }

    case 'error': {
      datadog.logger.error(message);
      break;
    }

    default: {
      datadog.logger.info(message);
    }
  }

  return true;
};
